import { MapPin, Mail, Facebook, Instagram, Youtube, Music2, Star } from "lucide-react";
import { business, social } from "@/data/siteConfig";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { PhoneDisplay, CallButton } from "@/components/CallButton";
import { ContactForm } from "@/components/ContactForm";
import { Reveal } from "@/components/Reveal";

const socialIcon = { Facebook, Instagram, YouTube: Youtube, TikTok: Music2, Yelp: Star };

const InfoCard = ({ icon: Icon, label, children }) => (
  <div className="border border-white/10 bg-ink-surface p-6">
    <div className="mb-3 flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <div className="leading-relaxed text-steel-light">{children}</div>
  </div>
);

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact"
        description="Contact Velasco Lock and Safe in Norwalk, CA. Call (562) 716-4447 for locksmith and safe services throughout Southern California. Phone is the fastest way to reach us."
        path="/contact"
      />
      <PageHero
        overline="Contact"
        title="Call Velasco Lock and Safe"
        description="Phone is the fastest way to reach us. Tell us about your lock or safe job and we'll let you know how we can help."
        showCall={false}
      />

      <section className="bg-ink py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="border border-gold/25 bg-gradient-to-br from-ink-surface to-ink p-8 md:p-10">
                <div className="mb-4 font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
                  Primary Contact · Phone
                </div>
                <PhoneDisplay size="text-4xl sm:text-5xl" testid="contact-page-phone" />
                <p className="mt-4 max-w-md leading-relaxed text-steel">
                  On desktop, click the number to copy it. On mobile, tap to call right away.
                </p>
                <div className="mt-6">
                  <CallButton size="lg" testid="contact-page-call-btn" />
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <InfoCard icon={MapPin} label="Based In">
                  {business.city}, {business.state}
                  <br />
                  <span className="text-steel">Serving {business.region}</span>
                </InfoCard>
                <InfoCard icon={Mail} label="Email">
                  <a
                    href={`mailto:${business.email}`}
                    className="break-all transition-colors hover:text-gold"
                  >
                    {business.email}
                  </a>
                </InfoCard>
              </div>

              <div className="mt-6 border border-white/10 bg-ink-surface p-6">
                <div className="mb-4 font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
                  Follow
                </div>
                <div className="flex flex-wrap gap-3">
                  {social.map((s) => {
                    const Icon = socialIcon[s.name] || Star;
                    return (
                      <a
                        key={s.name}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.name}
                        data-testid={`contact-social-${s.name.toLowerCase()}`}
                        className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/10 text-steel-light transition-colors duration-200 hover:border-gold/50 hover:text-gold"
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
                <p className="mt-4 text-xs text-steel/70">
                  Social profiles will be linked here once confirmed.
                </p>
              </div>
            </div>

            <Reveal>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
